'use client';

// based on https://ui.shadcn.com/docs/components/date-picker

import TextField from '../../inputs/TextField';
import styles from './datePicker.module.scss';
import { PatternFormat } from 'react-number-format';
import { useEffect, useState } from 'react';
import { parse, format, isValid } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '../../Popover';
import Button from '../../Button';
import { Calendar as CalendarIcon } from '../../../icons';
import Calendar from '../../Calendar';

const DEFAULT_MIN_DATE = new Date(
  new Date().setFullYear(new Date().getFullYear() - 100)
);
const DEFAULT_MAX_DATE = new Date(
  new Date().setFullYear(new Date().getFullYear() + 5)
);

export default function DatePicker({
  formatString = 'MM/dd/yyyy',
  startMonth = DEFAULT_MIN_DATE,
  endMonth = DEFAULT_MAX_DATE,
  selected,
  onChange,
  mode = 'single',
  disabledInput = false,
  ...props
}: React.ComponentPropsWithoutRef<typeof Calendar> & {
  selected?: Date;
  formatString?: string;
  onChange: (date: Date) => void;
  disabledInput?: boolean;
}) {
  const [inputValue, setInputValue] = useState(
    selected ? format(selected, formatString) : ''
  );
  const [month, setMonth] = useState<Date | undefined>(selected);
  const [popoverOpen, setPopoverOpen] = useState(false);

  useEffect(() => {
    if (selected) {
      setInputValue(format(selected, formatString));
      setMonth(selected);
    }
  }, [selected, formatString]);

  const clampDate = (date: Date) => {
    if (startMonth && date <= startMonth) {
      return startMonth;
    } else if (endMonth && date >= endMonth) {
      return endMonth;
    } else {
      return date;
    }
  };

  return (
    <div className={styles.wrapper}>
      <PatternFormat
        disabled={disabledInput}
        format="##/##/####"
        placeholder="MM / DD / YYYY"
        customInput={TextField}
        onBlur={() => {
          if (!selected) return;
          const trimmedValue = inputValue.trim();
          if (
            trimmedValue.length < 10 ||
            !isValid(parse(trimmedValue, formatString, new Date()))
          ) {
            setInputValue(format(selected, formatString));
          }
        }}
        onFocus={() => {
          setPopoverOpen(true);
        }}
        value={inputValue}
        onValueChange={({ formattedValue }) => {
          setInputValue(formattedValue);
          const trimmedValue = formattedValue.trim();
          if (trimmedValue.length >= 10) {
            const date = parse(trimmedValue, formatString, new Date());
            if (isValid(date)) {
              const clamppedDate = clampDate(date);
              setInputValue(format(clamppedDate, formatString));
              onChange(clamppedDate);
              setMonth(clamppedDate);
              setPopoverOpen(false);
            }
          }
        }}
      />
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <div className={styles.calendarTrigger}>
          <PopoverTrigger asChild>
            <Button
              className={styles.triggerButton}
              colorScheme="ghost"
              size="mini"
              disabled={disabledInput}
            >
              <CalendarIcon />
            </Button>
          </PopoverTrigger>
        </div>
        <PopoverContent
          onOpenAutoFocus={(e) => e.preventDefault()}
          alignOffset={-30}
          sideOffset={12}
          align="end"
        >
          <Calendar
            selected={selected}
            onSelect={(date) => {
              if (date) {
                onChange(date);
                setInputValue(format(date, formatString));
              }
            }}
            month={month}
            onMonthChange={(month) => setMonth(month)}
            disabled={{
              before: startMonth,
              after: endMonth
            }}
            mode={mode}
            startMonth={startMonth}
            endMonth={endMonth}
            {...props}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
